import { appConfiguration } from '../app-starter';
import { API_INSTANCE } from '../service-http';
import { getAppLocale, getAppLocaleAsObject } from '../app-locale';
import ProductInformationCache from '../../persistence/description-cache';
import { BUY_AUTOMATION_URLS, EXTERNAL_SYSTEM_EVENT_NAMES, WIDGET_WRAPPER_ADDTO, api_base_url, possible_starter_selections, preferredProductReference } from '../../constants';


const discoverBaseApiUrl = () => {
  const origin = window.location.origin;

  const { lang, country } = getAppLocaleAsObject();

  const correctedCountry = (country === 'GB' ? 'UK' : country);

  let result;
  // The URL for adding a configuration to My Products depends on the domain the application is run on.
  if (origin.match(/\bse\.com$/) || origin.match(/\bpre\.se\.com$/)) {
    // Single domain: post to https://www.se.com/ru/ru/product/my-products.
    result = `${origin}/${correctedCountry.toLowerCase()}/${lang.toLowerCase()}`;
  } else if (origin.match(/\bschneider-electric\b/)) {
    // A locale-specific domain: post to https://www.schneider-electric.com.au/en/product/my-products.
    result = `${origin}/${lang.toLowerCase()}`;
  } else {
    // It's workaround solution for local/pre-prod/sqe/dev etc env's
    result = `https://www.schneider-electric.${correctedCountry.toLowerCase() === 'ww' ? 'com' : correctedCountry.toLowerCase()}/${lang.toLowerCase()}`;
  }

  return result;
};

export function getCartUrl() {
  const baseApiUrl = discoverBaseApiUrl();
  return `${baseApiUrl}/product-cart`;
}

export async function addToBuyAutomation(configuration) {

  const payload = buildAddToBuyAutomationPayload(configuration);

  const environment = process.env.REACT_APP_SEAMLESS_ENV || 'prod';
  const buyAutomationURL = BUY_AUTOMATION_URLS[environment]?.ADD_TO_CART || BUY_AUTOMATION_URLS.prod.ADD_TO_CART;
  let token = appConfiguration.seamlessToken;

  // Try to get the token from the getUserDetails (MySchneider only).
  if (window && window.getUserDetails && window.getUserDetails()?.access_token != null) {
    try {
      token = window.getUserDetails()?.access_token;
    }
    catch(error) {
      console.warn('Failed to retrieve token from user details function, using script token.');
    }
  }

  return API_INSTANCE.post(`${api_base_url}/buy-automation?locale=${getAppLocale()}`,
    {
      buyAutomationURL,
      buyAutomationPayload: payload,
      token
    }).then(() => {}).catch((error) => {
      throw error;
    });
}

function sendToParentFrame(configuration, addToInformation) {
  return new Promise((successCallback, failureCallback) => {
    try {
      const payload = buildAddToMyProductsPayload(configuration, true, true);
      const topology = buildTopology(configuration);
      const eventPayload = { type: 'ScWidgetSaveConfigurationEvent', options: { configurationMasterId: configuration.id, locale: payload.locale, country: payload.country, products: payload.products, grouping: payload.grouping } };
      if (topology) {
        eventPayload.options.topology = topology;
      }
      if (addToInformation) {
        eventPayload.options.extras = {
          addTo: addToInformation
        };
      }
      window.parent.postMessage(eventPayload, '*');
    } catch (error) {
      failureCallback();
    }
    successCallback();
  });
}

export async function addToMyProject(configuration) {
  try {
    await ProductInformationCache.loadReferencesDescriptions(configuration.extractDistinctReferences());
  } catch(e) {}

  if (window.self !== window.top) {
    let addToInformation;
    if (appConfiguration.unifiedCheckoutAvailable) {
      addToInformation = WIDGET_WRAPPER_ADDTO.CART;
    }

    return sendToParentFrame(configuration, addToInformation);
  }

  const payload = buildAddToMyProductsPayload(configuration, false, true);

  return new Promise((successCallback, failureCallback) => {
    try {

      let eventName = EXTERNAL_SYSTEM_EVENT_NAMES.PUBLIC_JOURNEY_ADD_TO_PROJECT;
      if (appConfiguration.seamlessSession) {
        eventName = EXTERNAL_SYSTEM_EVENT_NAMES.MYSCHNEIDER_ADD_TO_PROJECT;
      }
      if (appConfiguration.unifiedCheckoutAvailable) {
        eventName = EXTERNAL_SYSTEM_EVENT_NAMES.UNIFIED_CHECKOUT_ADD_TO_CART;
      }
      document.dispatchEvent(
        new CustomEvent(eventName, {
          bubbles: true,
          detail: payload
        })
      );
    } catch (error) {
      failureCallback();
    }
    successCallback();
  });
}

export async function addToMyList(configuration) {
  try {
    await ProductInformationCache.loadReferencesDescriptions(configuration.extractDistinctReferences());
  } catch(e) {}

  if (window.self !== window.top) {
    let addToInformation;
    if (appConfiguration.unifiedCheckoutAvailable) {
      addToInformation = WIDGET_WRAPPER_ADDTO.LIST;
    }
    return sendToParentFrame(configuration, addToInformation);
  }

  const payload = buildAddToMyProductsPayload(configuration, false, true);

  return new Promise((successCallback, failureCallback) => {
    try {
      document.dispatchEvent(
        new CustomEvent(EXTERNAL_SYSTEM_EVENT_NAMES.UNIFIED_CHECKOUT_ADD_TO_LIST, {
          bubbles: true,
          detail: payload
        })
      );
    } catch (error) {
      failureCallback();
    }
    successCallback();
  });
}

function processTopologyModule(module) {
  const result = {id: module.id};
  const baseModule = module.slottedAdditionalDevices.find(m => m.layer === -1);
  if (baseModule) {
    result.base = {id: baseModule.selectedDeviceId};
  }

  const terminalBlock = module.slottedAdditionalDevices.find(m => m.layer === 1);
  if (terminalBlock && terminalBlock.selectedDevice) {
    result.terminalBlock = {id: terminalBlock.selectedDeviceId};
    result.terminalBlocks = [];
    for (let i = 0; i < terminalBlock.selectedDevice.quantity; i++) {
      result.terminalBlocks.push({id: terminalBlock.selectedDevice.id});
    }
  }

  return result;
}

/**
 * Expose the configuration topology (for Architecture builder export mainly).
 * This should only be called when doing a "Add to my products" as an Iframe (if done for public website, it will break the API /!\)
 * Current configuration types are available as a topology extract:
 * - PLC configurations
 * @param {*} configuration the configuration to extract the topology
 * @returns configuration topology (if the configuration is allowed to be exported), null otherwise
 */
function buildTopology(configuration) {

  let result = null;
  if (possible_starter_selections.PLC_IO === configuration.starterType || possible_starter_selections.EDGE === configuration.starterType) {
    result = {
      communicationModules: configuration.comModules.toReversed().map(m => { return {id: m.id}; }),
      mainModule: {id: configuration.headModule.id},
      ioModuleBlocks: configuration.ioModuleBlocks.map(b => { return {modules: b.modules.map(m => processTopologyModule(m)) };}),
      ioIslands: configuration.ioIslands.map(island => {
        return {
          protocol: island.protocol?.name,
          communicationModules: island.configuration.comModules.toReversed().map(m => { return {id: m.id}; }),
          mainModule: processTopologyModule(island.configuration.headModule),
          ioModuleBlocks: island.configuration.ioModuleBlocks.map(b => { return {modules: b.modules.map(m =>  processTopologyModule(m)) };})
        };
      })
    };
  }

  return result;
}

export async function addToMyProduct(configuration) {
  try {
    await ProductInformationCache.loadReferencesDescriptions(configuration.extractDistinctReferences());
  } catch(e) {}

  if (window.self !== window.top) {
    let addToInformation;
    if (appConfiguration.unifiedCheckoutAvailable) {
      addToInformation = WIDGET_WRAPPER_ADDTO.CART;
    }
    return sendToParentFrame(configuration, addToInformation);
  }

  const baseApiUrl = discoverBaseApiUrl();
  const favoritesUrl = `${baseApiUrl}/product/my-products`;


  if (appConfiguration.seamlessSession) {
    const payload = buildAddToMyProductsPayload(configuration, false, true);

    document.dispatchEvent(
      new CustomEvent('PLC_ADD_TO_MY_PRODUCTS', {
        bubbles: true,
        detail: payload
      })
    );
  }

  const payload = buildAddToMyProductsPayload(configuration, false, false);

  return API_INSTANCE.post(favoritesUrl, payload, false).then(({ data: getData }) => {
  }).catch((error) => {
    throw error;
  });
}

function createProductObject(productId, quantity, isInWrapperMode) {
  const description = ProductInformationCache.getDescription(productId)
  if (isInWrapperMode) {
    const distributorPayload = {
      reference: productId,
      quantity,
      description,
      pictureUrl: `https://gw-api.schneider-electric.com/sc/products/image/${productId}?width=100&height=100`,
    };

    if (preferredProductReference.ean === appConfiguration.preferredProductReference) {
      distributorPayload.ean = ProductInformationCache.getEAN(productId);
    } else if (preferredProductReference.enum === appConfiguration.preferredProductReference) {
      distributorPayload.enumber = ProductInformationCache.getENumber(productId);
    }

    return distributorPayload;
  } else {
    return { productId, quantity, description };
  }
}

function buildAddToBuyAutomationPayload(configuration) {
  const items = [];
  aggregatedReferences(configuration.extractReferencesForAddToCart().extractSortedReferences())
    .forEach(aggregatedReference => {
      items.push({'PartNumber': aggregatedReference.reference, 'Quantity': aggregatedReference.quantity });
    });

  return {'LineItems': items, "BasketName": configuration.name};
}

function buildAddToMyProductsPayload(configuration, isInWrapperMode, includeLocale) {
  const payload = {
    products: []
  };

  if (includeLocale) {
    payload.locale = getAppLocale();
    payload.country = getAppLocaleAsObject().country;
  }

  aggregatedReferences(configuration.extractReferencesForAddToCart().extractSortedReferences())
    .forEach(aggregatedReference => {
      payload.products.push(createProductObject(aggregatedReference.reference, aggregatedReference.quantity, isInWrapperMode));
    });

  payload.grouping = {
    enable: true,
    backURL: `${appConfiguration.backUrl}?configurationId=${configuration.id}`,
    configurationMasterId: configuration.id,
    displayName: configuration.name
  };
  payload.options = {
    allowPartialAdding: true,
    source: 'MSX'
  };


  return payload;
}

export function aggregatedReferences(references) {
  let maxNumber = 1;
  const map = {};
  references.forEach((referenceObj) => {
    if (map[referenceObj.reference]) {
      let obj = map[referenceObj.reference];
      map[referenceObj.reference] = { ...obj, quantity: obj.quantity + referenceObj.quantity }

    } else {
      map[referenceObj.reference] = {
        order: maxNumber++,
        reference: referenceObj.reference,
        quantity: referenceObj.quantity
      };
    }
  });

  const aggregatedReferences = [];
  Object.keys(map).forEach((referenceID) => {
    aggregatedReferences.push(map[referenceID]);
  });

  aggregatedReferences.sort(function (a, b) { return a.order - b.order });
  return aggregatedReferences;
}
